import { useState, useMemo, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Trash2, Loader2, Save, Send, Paperclip, X, Upload, Wand2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { formatINR } from "@/lib/constants";
import { DivisionTag } from "@/components/StatusBadge";
import { PageHeader } from "@/components/PageHeader";
import { GuidedProductDialog } from "@/components/GuidedProductDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ErpItemSearch } from "@/components/ErpItemSearch";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const quoteImgUrl = (path) => {
  const t = localStorage.getItem("sc_token");
  return t && path ? `${BACKEND}/api/quotations/image/${path}?auth=${t}` : "";
};
const BASE_UNITS = ["Sq. Feet", "Sq. Mtr", "Nos", "Rmt"];
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

export default function QuoteBuilder() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const editId = params.get("quote");
  const [items, setItems] = useState([]);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [oppId, setOppId] = useState(params.get("opp"));
  const [leadId, setLeadId] = useState(params.get("lead"));
  const [uploadingIdx, setUploadingIdx] = useState(null);
  const [configUploading, setConfigUploading] = useState(false);
  const [guidedOpen, setGuidedOpen] = useState(false);

  const { data: existing } = useQuery({
    queryKey: ["quote-edit", editId],
    queryFn: async () => (await api.get(`/quotations/${editId}`)).data,
    enabled: !!editId,
  });

  useEffect(() => {
    if (existing) {
      setItems((existing.line_items || []).map((it) => {
        const lp = Number(it.list_price ?? it.unit_price ?? 0);
        const fp = it.final_price != null && it.final_price !== "" ? Number(it.final_price) : round2(lp * (1 - (Number(it.discount_pct) || 0) / 100));
        return { ...it, list_price: lp, final_price: fp, unit: it.unit || "Nos" };
      }));
      setNotes(existing.notes || "");
      setOppId(existing.opportunity_id);
      setLeadId(existing.lead_id || null);
    }
  }, [existing]);

  const { data: opp, isLoading } = useQuery({
    queryKey: ["deal", oppId],
    queryFn: async () => (await api.get(`/opportunities/${oppId}`)).data,
    enabled: !!oppId,
  });

  const { data: lead, isLoading: leadLoading } = useQuery({
    queryKey: ["lead-for-quote", leadId],
    queryFn: async () => (await api.get(`/leads/${leadId}`)).data,
    enabled: !!leadId && !oppId,
  });
  const src = opp || lead;

  const { data: products = [] } = useQuery({
    queryKey: ["products", src?.division_id],
    queryFn: async () => (await api.get("/products", { params: { division_id: src.division_id } })).data,
    enabled: !!src?.division_id,
  });

  const addProduct = (pid) => {
    const p = products.find((x) => x.id === pid);
    if (!p) return;
    setItems((it) => [...it, { product_id: p.id, product_name: p.name, erp_product_code: p.erp_product_code || "", unit: p.unit || "Nos", qty: 1, list_price: p.base_price, discount_pct: 0, final_price: p.base_price }]);
  };
  const addErpItem = (it) => {
    const dtp = Number(it.dtp_price || 0);
    if (!dtp) toast.info("No DTP price on file for this item — enter the List Price manually");
    setItems((prev) => [...prev, { product_id: null, erp_item_code: it.item_code, product_name: it.description, erp_product_code: it.stock_code, unit: it.uom || "Nos", qty: 1, list_price: dtp, discount_pct: 0, final_price: dtp }]);
  };

  const update = (i, k, v) => setItems((prev) => prev.map((row, idx) => {
    if (idx !== i) return row;
    const r = { ...row, [k]: v };
    const lp = Number(k === "list_price" ? v : r.list_price) || 0;
    if (k === "discount_pct") {
      r.final_price = round2(lp * (1 - (Number(v) || 0) / 100));
    } else if (k === "final_price") {
      r.discount_pct = lp > 0 ? round2((1 - (Number(v) || 0) / lp) * 100) : r.discount_pct;
    } else if (k === "list_price") {
      r.final_price = round2((Number(v) || 0) * (1 - (Number(r.discount_pct) || 0) / 100));
    }
    return r;
  }));
  const remove = (i) => setItems((it) => it.filter((_, idx) => idx !== i));

  const uploadImage = async (i, file) => {
    if (!file) return;
    setUploadingIdx(i);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/quotations/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setItems((prev) => prev.map((row, idx) => idx === i ? { ...row, image: data.storage_path, image_thumb: data.thumb_path || "" } : row));
      toast.success("Attachment added");
    } catch (e) { toast.error(apiError(e)); } finally { setUploadingIdx(null); }
  };

  const uploadConfigurator = async (file) => {
    if (!file) return;
    setConfigUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/quotations/configurator-import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setItems((prev) => [...prev, {
        product_id: null, product_name: data.product_name, erp_product_code: "", unit: "Nos", qty: 1,
        list_price: 0, discount_pct: 0, final_price: 0,
        image: data.storage_path, image_thumb: data.thumb_path || "",
        configurator_params: data.params || [],
      }]);
      toast.success(data.params?.length
        ? "Configurator selection added — set the quantity and price"
        : "Sheet attached — couldn't read the parameters, please edit the product details");
    } catch (e) { toast.error(apiError(e)); } finally { setConfigUploading(false); }
  };

  const computed = useMemo(() => items.map((it) => {
    const qty = Number(it.qty || 0);
    const lp = Number(it.list_price || 0);
    const fp = Number(it.final_price || 0);
    return { ...it, line_total: round2(qty * fp), disc_amount: round2(Math.max(qty * (lp - fp), 0)) };
  }), [items]);
  const subtotal = computed.reduce((a, b) => a + Number(b.qty || 0) * Number(b.list_price || 0), 0);
  const discountTotal = computed.reduce((a, b) => a + b.disc_amount, 0);
  const grand = computed.reduce((a, b) => a + b.line_total, 0);
  const overallPct = subtotal > 0 ? (discountTotal / subtotal) * 100 : 0;
  const needsApproval = overallPct > 0.001;

  const save = async (submit) => {
    if (items.length === 0) { toast.error("Add at least one line item"); return; }
    setSaving(true);
    const payload = { opportunity_id: oppId || null, lead_id: oppId ? null : leadId, line_items: computed.map(({ disc_amount, ...rest }) => rest), notes };
    try {
      let qid = editId;
      if (editId) {
        await api.put(`/quotations/${editId}`, { line_items: payload.line_items, notes });
      } else {
        const { data } = await api.post("/quotations", payload);
        qid = data.id;
      }
      if (submit) await api.post(`/quotations/${qid}/submit`);
      toast.success(submit ? (needsApproval ? "Quotation submitted to Sales Head for approval" : "Quotation submitted — auto-approved (no discount)") : "Saved");
      navigate(`/quotes/${qid}`);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  if (!oppId && !leadId && !editId) return <div className="text-center py-20 text-slate-400">Open a lead or an opportunity and click “Create Quotation”.</div>;
  if (isLoading || leadLoading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  return (
    <div>
      <button onClick={() => navigate(oppId ? `/deals/${oppId}` : leadId ? `/leads/${leadId}` : "/quotes")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4" data-testid="back-btn">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
      <PageHeader title={editId ? "Edit Quotation" : "New Quotation / BOQ"} subtitle={`${opp?.name || lead?.project_name || lead?.name || ""} · ${src?.company || ""}${!oppId && leadId ? " · quoting a Lead" : ""}`}>
        <DivisionTag id={src?.division_id} size="md" />
      </PageHeader>

      <Card className="p-5 mb-4">
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          <Label className="text-xs whitespace-nowrap">Add product (Milestone code)</Label>
          <ErpItemSearch onSelect={addErpItem} />
          <span className="text-[11px] text-slate-400">Type the first letters of a product code or description · DTP auto-fills when on file</span>
        </div>
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          <Label className="text-xs whitespace-nowrap">Add product from catalog</Label>
          <Select onValueChange={addProduct} value="">
            <SelectTrigger className="h-9 max-w-md" data-testid="product-picker"><SelectValue placeholder="Select a product…" /></SelectTrigger>
            <SelectContent className="max-h-72">
              {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}{p.erp_product_code ? ` [${p.erp_product_code}]` : ""} — {formatINR(p.base_price)}/{p.unit}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <Label className="text-xs whitespace-nowrap">Add from configurator</Label>
          <Button type="button" variant="outline" className="h-9 gap-1.5 text-slate-600 hover:border-brand hover:text-brand" onClick={() => setGuidedOpen(true)} data-testid="guided-picker-btn">
            <Wand2 className="h-4 w-4" /> Guided product picker
          </Button>
          <label className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-slate-200 text-sm cursor-pointer hover:border-brand hover:text-brand transition-colors ${configUploading ? "opacity-60 pointer-events-none" : "text-slate-600"}`}>
            <input type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" disabled={configUploading}
              onChange={(e) => { uploadConfigurator(e.target.files?.[0]); e.target.value = ""; }} data-testid="configurator-upload" />
            {configUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {configUploading ? "Reading parameters…" : "Upload configurator sheet"}
          </label>
          <span className="text-[11px] text-slate-400">Pick step-by-step with live configurator options, or upload a downloaded configurator sheet</span>
        </div>

        <div className="overflow-x-auto border border-slate-200 rounded-md">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              {["Product", "Qty", "Unit", "List Price (DTP)", "Disc %", "Final Price", "Total", ""].map((h) => <TableHead key={h} className="text-xs uppercase tracking-wider text-slate-500 whitespace-nowrap">{h}</TableHead>)}
            </TableRow></TableHeader>
            <TableBody>
              {computed.map((it, i) => (
                <TableRow key={i} data-testid={`line-item-${i}`}>
                  <TableCell className="font-medium min-w-[220px] align-top">
                    {it.product_name}
                    {it.erp_product_code
                      ? <span className="block font-mono text-[11px] text-emerald-600 font-normal mt-0.5">Code: {it.erp_product_code}</span>
                      : <span className="block text-[11px] text-slate-300 font-normal mt-0.5">no product code</span>}
                    {it.configurator_params?.length > 0 && (
                      <span className="block text-[10px] text-slate-500 font-normal mt-0.5" data-testid={`line-config-params-${i}`}>
                        {it.configurator_params.map((p) => `${p.label}: ${p.value}`).join(" · ")}
                      </span>
                    )}
                    <div className="mt-1.5">
                      {it.image ? (
                        <div className="relative inline-block">
                          <a href={quoteImgUrl(it.image)} target="_blank" rel="noreferrer" title="Open original">
                            <img src={quoteImgUrl(it.image_thumb || it.image)} alt="item" className="h-16 max-w-[140px] rounded border border-slate-200 object-cover" data-testid={`line-image-preview-${i}`} onError={(e) => { e.currentTarget.style.opacity = 0.3; }} />
                          </a>
                          <button onClick={() => setItems((prev) => prev.map((row, idx) => idx === i ? { ...row, image: "", image_thumb: "" } : row))} className="absolute -top-1.5 -right-1.5 bg-white rounded-full border border-slate-200 text-slate-400 hover:text-red-500 p-0.5" data-testid={`line-image-remove-${i}`}><X className="h-3 w-3" /></button>
                        </div>
                      ) : (
                        <label className="inline-flex items-center gap-1 text-[11px] text-slate-400 hover:text-brand cursor-pointer font-normal" title="Attach a photo or PDF — shows as a small inset">
                          <input type="file" accept=".jpg,.jpeg,.png,.pdf" className="hidden" onChange={(e) => { uploadImage(i, e.target.files?.[0]); e.target.value = ""; }} data-testid={`line-image-${i}`} />
                          {uploadingIdx === i ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Paperclip className="h-3.5 w-3.5" />} Attach photo / PDF
                        </label>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="align-top"><Input type="number" value={it.qty} onChange={(e) => update(i, "qty", e.target.value)} className="h-8 w-20" data-testid={`line-qty-${i}`} /></TableCell>
                  <TableCell className="align-top">
                    <Select value={it.unit || "Nos"} onValueChange={(v) => update(i, "unit", v)}>
                      <SelectTrigger className="h-8 w-[100px]" data-testid={`line-unit-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{[...new Set([...(it.unit ? [it.unit] : []), ...BASE_UNITS])].map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="align-top"><Input type="number" value={it.list_price} onChange={(e) => update(i, "list_price", e.target.value)} className="h-8 w-28" data-testid={`line-list-price-${i}`} /></TableCell>
                  <TableCell className="align-top"><Input type="number" value={it.discount_pct} onChange={(e) => update(i, "discount_pct", e.target.value)} className="h-8 w-20" data-testid={`line-disc-${i}`} /></TableCell>
                  <TableCell className="align-top"><Input type="number" value={it.final_price} onChange={(e) => update(i, "final_price", e.target.value)} className="h-8 w-28" data-testid={`line-final-price-${i}`} /></TableCell>
                  <TableCell className="font-mono font-medium whitespace-nowrap align-top" data-testid={`line-total-${i}`}>{formatINR(it.line_total)}</TableCell>
                  <TableCell className="align-top"><button onClick={() => remove(i)} className="text-slate-400 hover:text-red-500" data-testid={`line-remove-${i}`}><Trash2 className="h-4 w-4" /></button></TableCell>
                </TableRow>
              ))}
              {items.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-400">No line items. Add products above.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>

        <div className="flex flex-col items-end gap-1 mt-4 text-sm">
          <div className="flex gap-8"><span className="text-slate-500">Subtotal (list)</span><span className="font-mono w-32 text-right">{formatINR(subtotal)}</span></div>
          <div className="flex gap-8"><span className="text-slate-500">Discount</span><span className="font-mono w-32 text-right text-red-600">- {formatINR(discountTotal)}</span></div>
          <div className="flex gap-8 text-base font-semibold border-t pt-1 mt-1"><span>Grand Total</span><span className="font-mono w-32 text-right" data-testid="grand-total">{formatINR(grand)}</span></div>
          {subtotal > 0 && (
            <div className={`mt-2 rounded-md px-3 py-1.5 text-xs font-medium ${needsApproval ? "bg-amber-50 text-amber-700 border border-amber-200" : "bg-emerald-50 text-emerald-700 border border-emerald-200"}`} data-testid="discount-hint">
              Overall discount {overallPct.toFixed(1)}% — {needsApproval ? "routes to Nats (Sales Head) for approval" : "auto-approves on submit (no discount)"}
            </div>
          )}
        </div>
      </Card>

      <Card className="p-5 mb-4">
        <Label className="text-xs">Notes / Terms</Label>
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="mt-1.5" placeholder="Payment terms, delivery timeline, validity…" data-testid="quote-notes" />
      </Card>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => save(false)} disabled={saving} className="gap-1.5" data-testid="save-draft-btn"><Save className="h-4 w-4" /> Save {editId ? "" : "Draft"}</Button>
        <Button onClick={() => save(true)} disabled={saving} className="bg-slate-900 hover:bg-slate-800 gap-1.5" data-testid="submit-approval-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {needsApproval ? "Submit for Approval" : "Submit (auto-approve)"}</Button>
      </div>

      <GuidedProductDialog open={guidedOpen} onOpenChange={setGuidedOpen} onAdd={(item) => setItems((prev) => [...prev, item])} />
    </div>
  );
}
